import React, { useState, useEffect, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faCalendarCheck, 
  faBullhorn, 
  faPaintBrush, 
  faStarHalfAlt,
  faSearch 
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { InputField } from "@cakrawalajakarta/shared-components";

const CariAnggota = lazy(() => import("./CariAnggota").then(module => ({ default: module.CariAnggota })));
const DetailAnggota = lazy(() => import("./DetailAnggota").then(module => ({ default: module.DetailAnggota })));

const services = [
  {
    title: "Event Management",
    description: "Pengelolaan acara profesional dari konsep hingga pelaksanaan, dengan fokus pada detail dan pengalaman peserta.",
    icon: faCalendarCheck,
    link: "/Kontak"
  },
  {
    title: "Digital Marketing",
    description: "Strategi pemasaran digital komprehensif untuk meningkatkan visibilitas dan engagement brand Anda.",
    icon: faBullhorn,
    link: "/Kontak"
  },
  {
    title: "Creative Content",
    description: "Produksi konten kreatif yang menarik dan sesuai dengan identitas brand Anda.",
    icon: faPaintBrush,
    link: "/Kontak"
  },
  {
    title: "Brand Development",
    description: "Pengembangan brand yang kuat dan konsisten untuk membangun kepercayaan pelanggan.",
    icon: faStarHalfAlt,
    link: "/Kontak"
  }
];

export const Layanan = () => {
  const [memberId, setMemberId] = useState("");
  const [searchError, setSearchError] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [formData, setFormData] = useState({
    memberId: ""
  });
  const navigate = useNavigate();

  const memberships = [
    {
      title: "Kartu Akses ALTI DKI",
      membershipId: "ALTI_MEMBERSHIP",
      price: 100000,
      slug: "membership",
      validity: "1 Tahun",
      benefit: "Akses Eksklusif",
      date: "Berlaku Setiap Saat",
    },
  ];

  const handleRegister = (membership) => {
    navigate(`/form/${membership.slug}`, {
      state: {
        title: membership.title,
        price: membership.price,
        membershipId: membership.membershipId,
      },
    });
  };

  const handleMemberSearch = async (e) => {
    e.preventDefault();
    if (!memberId.trim()) {
      setSearchError("Masukkan ID Anggota!");
      return;
    }

    setSearchLoading(true);
    setSearchError("");

    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}/api/member/${memberId.trim()}`
      );
      navigate(`/DetailAnggota/${memberId.trim()}`);
    } catch (err) {
      setSearchError("Anggota tidak ditemukan atau terjadi kesalahan.");
    } finally {
      setSearchLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    setMemberId(value);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="w-full min-h-screen text-center text-cyan-600 flex-col justify-center relative bg-gradient-to-b from-slate-50 to-slate-100">
      <div className="container mx-auto px-4 py-20">
        <>
          <motion.h2
            className="text-base sm:text-lg md:text-xl font-normal uppercase mb-1 md:mb-2 text-cyan-600"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            PT CAKRAWALA JAKARTA KREATIF
          </motion.h2>

          <motion.p
            className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black mx-auto mb-8 md:mb-16 text-cyan-600 px-4"
            initial={{ opacity: 0, y: -15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            LAYANAN KAMI
          </motion.p>

          <div className="max-w-6xl mx-auto space-y-4">
            <motion.div
              className="text-left"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <img 
                      src="/logo-alti.png" 
                      alt="ALTI DKI Logo" 
                      className="h-24 md:h-32 w-auto"
                    />
                    <h2 className="text-2xl font-bold text-cyan-900 mb-6">
                      Kartu Akses ALTI DKI
                    </h2>
                    <div className="prose text-cyan-800">
                      <p className="mb-4">
                        Kartu Akses ALTI DKI memberikan Anda akses eksklusif ke berbagai fasilitas dan kegiatan ALTI DKI Jakarta. Dengan kartu ini, Anda akan mendapatkan:
                      </p>
                      <ul className="list-disc pl-5 mb-6 space-y-2">
                        <li>Akses ke semua event ALTI DKI</li>
                        <li>Diskon khusus untuk merchandise resmi</li>
                        <li>Prioritas pendaftaran event</li>
                        <li>Jaringan komunitas trail running</li>
                        <li>Newsletter eksklusif kegiatan</li>
                      </ul>
                      <p>Masa berlaku kartu adalah 1 tahun sejak tanggal pendaftaran.</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-slate-100 p-4 border border-slate-200 rounded-xl">
                      <h3 className="text-xl font-bold text-cyan-900 mb-4">Biaya Keanggotaan</h3>
                      <p className="text-3xl font-bold text-cyan-600 mb-4">Rp 100.000</p>
                      <p className="text-cyan-800 mb-6">Per tahun</p>
                      <button
                        onClick={() => handleRegister(memberships[0])}
                        className="w-full py-3 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 transition-colors"
                      >
                        Daftar Sekarang
                      </button>
                    </div>

                    <div className="bg-slate-100 p-4 border border-slate-200 rounded-xl">
                      <h3 className="text-xl font-bold text-cyan-900 mb-4">
                        Cari Anggota
                      </h3>
                      <form onSubmit={handleMemberSearch} className="space-y-4">
                        <InputField
                          label="ID Anggota"
                          name="memberId"
                          type="text"
                          value={formData.memberId}
                          onChange={handleChange}
                          placeholder="Contoh: ALTI-000001"
                          error={searchError}
                          labelColor="text-cyan-900"
                        />
                        <button
                          type="submit"
                          disabled={searchLoading}
                          className="w-full py-3 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 transition-colors flex items-center justify-center gap-2"
                        >
                          {searchLoading ? (
                            <>
                              <span>Mencari...</span>
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faSearch} />
                              <span>Cari Anggota</span>
                            </>
                          )}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-4">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  className="bg-white rounded-xl p-4 border border-slate-200"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <FontAwesomeIcon 
                    icon={service.icon} 
                    className="text-4xl mb-4 text-cyan-600"
                  />
                  <h3 className="text-xl font-bold text-cyan-900 mb-3">{service.title}</h3>
                  <p className="text-cyan-800 mb-6">{service.description}</p>
                  <Link
                    to={service.link}
                    className="inline-block bg-cyan-600 text-white px-6 py-2 rounded-xl hover:bg-cyan-700 transition-colors duration-200"
                  >
                    Pelajari Lebih Lanjut
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </>
      </div>
    </section>
  );
};
